import { ref, watch } from 'vue';

export const useOptimisticSave = <TValue>(propSource: () => TValue) => {
	const committedValue = ref<TValue>(propSource());
	const latestSaveRequestId = ref(0);
	const pendingSaveCount = ref(0);

	watch(propSource, (next) => {
		if (pendingSaveCount.value > 0) {
			return;
		}

		committedValue.value = next;
	});

	const save = async (
		optimisticValue: TValue,
		performSave: () => Promise<TValue | undefined>
	) => {
		latestSaveRequestId.value += 1;
		const requestId = latestSaveRequestId.value;
		const previousValue = committedValue.value;

		committedValue.value = optimisticValue;

		const isStale = () => requestId !== latestSaveRequestId.value;

		pendingSaveCount.value += 1;

		try {
			const serverValue = await performSave();

			if (isStale()) {
				return;
			}

			if (serverValue !== undefined) {
				committedValue.value = serverValue;
			}
		} catch {
			if (isStale()) {
				return;
			}

			committedValue.value = previousValue;
		} finally {
			pendingSaveCount.value -= 1;
		}
	};

	return {
		committedValue,
		save,
	};
};
